import type { ComponentProps, RefObject, JSX } from 'react'

import type { UseCommentReplyFormReturn } from './hooks/useCommentReplyForm'
import { useCommunityConfig } from '../../CommunityConfig'
import { EngagementReplyForm } from '../../components/EngagementReplyForm'
import { useCommentData } from '../context/CommentContext'
import { CommentReplyFormHeader } from '../CommentsList/CommentReplyFormHeader'

type CommentReplyFormProps = {
  channel: ComponentProps<typeof EngagementReplyForm>['channel']
  reply: UseCommentReplyFormReturn
  textareaRef: RefObject<HTMLTextAreaElement>
  expanded: boolean
  canReply: boolean
}

/**
 * Comment reply area. Reads comment context and feeds the shared
 * `EngagementReplyForm` the comment-specific slots — the suggestions header
 * (whose AI chips are gated on channel support), aria label, assets, analytics
 * CTA, and the processing-row test id. The shared component owns the
 * processing-vs-form gating; reply state lives in the parent's `useCommentReplyForm`.
 */
export const CommentReplyForm = ({
  channel,
  reply,
  textareaRef,
  expanded,
  canReply,
}: CommentReplyFormProps): JSX.Element => {
  const comment = useCommentData()
  const {
    config: { supportAIFeatures },
  } = useCommunityConfig()

  // The "Replying to @user" label always renders; only the AI suggestion chips
  // inside it depend on channel support.
  const supportsSuggestions = supportAIFeatures(channel)

  return (
    <EngagementReplyForm
      reply={reply}
      channel={channel}
      textareaRef={textareaRef}
      expanded={expanded}
      canReply={canReply}
      id={`${comment.id}-reply`}
      ariaLabel={`Reply to comment from ${comment.author.username}`}
      processingTestId="comment-item"
      assets={comment.assets}
      cta="publish-comments-replyForm-newPost-1"
      suggestions={
        <CommentReplyFormHeader
          onSuggestionClick={reply.insertSuggestion}
          supportsSuggestions={supportsSuggestions}
        />
      }
    />
  )
}
